import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PublicPortalLayout from '../../components/public/PublicPortalLayout';
import PublicRequestForm from '../../components/public/PublicRequestForm';
import RequestTracker from '../../components/public/RequestTracker';

// Matches the baseURL used by axios in api.ts
// Works on dev (port 6160 → hits localhost/api via absolute URL)
// Works on prod (same domain, relative would also work but this is explicit)
const getApiBase = () =>
  process.env.REACT_APP_API_URL || 'http://localhost/api';

type PortalView = 'landing' | 'request' | 'success' | 'track';

export interface CompanyInfo {
  name: string;
  domain: string;
  logo?: string;
  primaryColor?: string;
}

export interface SubmittedRequest {
  referenceNumber: string;
  email: string;
  purpose: string;
  destination: string;
  startDateTime: string;
  endDateTime: string;
}

const StaffRequestPortal: React.FC = () => {
  // Token comes from the URL: /request-portal/:portalToken
  const { portalToken = '' } = useParams<{ portalToken: string }>();

  const [view, setView]                       = useState<PortalView>('landing');
  const [companyInfo, setCompanyInfo]         = useState<CompanyInfo | null>(null);
  const [submittedRequest, setSubmittedRequest] = useState<SubmittedRequest | null>(null);
  const [loading, setLoading]                 = useState(true);
  const [invalidToken, setInvalidToken]       = useState(false);

  useEffect(() => {
    if (!portalToken) {
      setInvalidToken(true);
      setLoading(false);
      return;
    }
    loadCompanyInfo();

    const params = new URLSearchParams(window.location.search);
    if (params.get('track')) setView('track');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalToken]);

  const loadCompanyInfo = async () => {
    try {
      const response = await fetch(`${getApiBase()}/public/${portalToken}/info`);
      const data = await response.json();
      if (data.success) {
        setCompanyInfo(data.data);
      } else {
        setInvalidToken(true);
      }
    } catch {
      setInvalidToken(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <PortalLoadingScreen />;

  if (invalidToken || !portalToken) return <InvalidPortalScreen />;

  return (
    <PublicPortalLayout companyInfo={companyInfo}>
      {view === 'landing' && (
        <LandingView
          onRequestVehicle={() => setView('request')}
          onTrackRequest={() => setView('track')}
        />
      )}
      {view === 'request' && (
        <PublicRequestForm
          companyDomain={portalToken}
          onSuccess={(req) => { setSubmittedRequest(req); setView('success'); }}
          onBack={() => setView('landing')}
        />
      )}
      {view === 'success' && submittedRequest && (
        <SuccessView
          request={submittedRequest}
          onTrackRequest={() => setView('track')}
          onNewRequest={() => setView('landing')}
        />
      )}
      {view === 'track' && (
        <RequestTracker
          companyDomain={portalToken}
          onBack={() => setView('landing')}
        />
      )}
    </PublicPortalLayout>
  );
};

// ─── Landing View ────────────────────────────────────────────────────────────
const LandingView: React.FC<{
  onRequestVehicle: () => void;
  onTrackRequest: () => void;
}> = ({ onRequestVehicle, onTrackRequest }) => (
  <div className="landing-view">
    <div className="landing-hero">
      <div className="hero-icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <rect width="48" height="48" rx="14" fill="currentColor" fillOpacity="0.1" />
          <path d="M8 30h32M12 30l2-8h20l2 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <circle cx="16" cy="33" r="2.5" stroke="currentColor" strokeWidth="2" />
          <circle cx="32" cy="33" r="2.5" stroke="currentColor" strokeWidth="2" />
        </svg>
      </div>
      <h1 className="hero-title">Vehicle Request</h1>
      <p className="hero-subtitle">
        Request a company vehicle for your trip. Fast, simple, and fully tracked.
      </p>
    </div>

    <div className="action-cards">
      <button className="action-card action-card--primary" onClick={onRequestVehicle}>
        <div className="action-card__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="action-card__content">
          <span className="action-card__title">Request a Vehicle</span>
          <span className="action-card__desc">Submit a new vehicle request</span>
        </div>
        <svg className="action-card__arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <button className="action-card action-card--secondary" onClick={onTrackRequest}>
        <div className="action-card__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="action-card__content">
          <span className="action-card__title">Track my Request</span>
          <span className="action-card__desc">Check status with reference number</span>
        </div>
        <svg className="action-card__arrow" width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
    </div>

    <div className="landing-steps">
      <p className="steps-label">How it works</p>
      <div className="steps-list">
        {[
          { num: '1', text: 'Fill in your trip details' },
          { num: '2', text: 'Get a reference number instantly' },
          { num: '3', text: 'Fleet manager reviews & approves' },
          { num: '4', text: 'Receive vehicle & driver details by email' },
        ].map((s) => (
          <div key={s.num} className="step-item">
            <span className="step-num">{s.num}</span>
            <span className="step-text">{s.text}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ─── Success View ────────────────────────────────────────────────────────────
const SuccessView: React.FC<{
  request: SubmittedRequest;
  onTrackRequest: () => void;
  onNewRequest: () => void;
}> = ({ request, onTrackRequest, onNewRequest }) => (
  <div className="success-view">
    <div className="success-icon">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <circle cx="28" cy="28" r="28" fill="currentColor" fillOpacity="0.1" />
        <path d="M18 28l7 7 13-14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
    <h2 className="success-title">Request Submitted!</h2>
    <p className="success-subtitle">
      Your vehicle request has been received and is pending approval.
    </p>

    <div className="reference-card">
      <p className="reference-label">Your Reference Number</p>
      <p className="reference-number">{request.referenceNumber}</p>
      <p className="reference-hint">Save this number to track your request</p>
    </div>

    <div className="success-details">
      <div className="detail-row">
        <span className="detail-label">Destination</span>
        <span className="detail-value">{request.destination}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Purpose</span>
        <span className="detail-value">{request.purpose}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">Updates sent to</span>
        <span className="detail-value">{request.email}</span>
      </div>
    </div>

    <div className="success-notice">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <p>You'll receive an email once a fleet manager approves your request, with vehicle and driver details.</p>
    </div>

    <div className="success-actions">
      <button className="btn btn--primary btn--full" onClick={onTrackRequest}>
        Track this Request
      </button>
      <button className="btn btn--ghost btn--full" onClick={onNewRequest}>
        Back to Home
      </button>
    </div>
  </div>
);

// ─── Loading Screen ───────────────────────────────────────────────────────────
const PortalLoadingScreen: React.FC = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f0f4f8',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  }}>
    <div style={{ textAlign: 'center' }}>
      <div className="loading-spinner" />
      <p style={{ marginTop: 16, color: '#64748b', fontSize: 14, fontWeight: 500 }}>
        Loading portal...
      </p>
    </div>
  </div>
);

// ─── Invalid Token Screen ─────────────────────────────────────────────────────
const InvalidPortalScreen: React.FC = () => (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#f0f4f8',
    fontFamily: "'Plus Jakarta Sans', sans-serif", padding: 24,
  }}>
    <div style={{ textAlign: 'center', maxWidth: 360 }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
        Invalid Portal Link
      </h2>
      <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
        This portal link is invalid or has been disabled.
        Please contact your fleet manager for the correct link.
      </p>
    </div>
  </div>
);

export default StaffRequestPortal;